import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconLanguage, IconLoader2, IconMessage, IconMessageFilled } from "@tabler/icons-react";

import { Tooltip, Text } from "@fragments";

import { CAPTION_LANGUAGES, CAPTION_LANGUAGE_NAMES } from "../../captionsLanguages";

/**
 * CaptionsPanel — Netflix-style two-column popover for the settings pill:
 * "Idioma" (audio language — display-only until audio tracks ship, every
 * item disabled as "Em breve") and "Legendas" (off + the caption tracks).
 *
 * Deliberately NOT built on the Menu/Popper fragments: the two-column
 * layout doesn't fit their single-list option model. It anchors above the
 * trigger inside the player subtree, so the fullscreen visibility whitelist
 * (`.player.fullscreen *`) covers it without portal special-casing.
 *
 * The API projection (`captions`) drives the chrome states: no entries →
 * no button; entries but none done → inert "generating" spinner; otherwise
 * the panel. Languages `done` in the API but absent from the master
 * playlist (stale cached master) render disabled — auto-enable is gated
 * the same way in useCaptions.
 */
function CaptionsPanel({ captions, availableLangs, activeLang, naturalLanguage, chromeHidden, onSelect }) {
    const { t } = useTranslation("content");
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const closeTimerRef = useRef(null);

    // Hover-driven (mouse): enter opens, leaving the trigger+panel subtree
    // closes after a short grace so crossing the gap doesn't flicker (the
    // panel's ::after bridge covers the gap itself). Touch/pen has no hover —
    // tap opens, outside-tap/Escape close.
    const cancelScheduledClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const handlePointerEnter = (event) => {
        if (event.pointerType !== "mouse") return;
        cancelScheduledClose();
        setOpen(true);
    };

    const handlePointerLeave = (event) => {
        if (event.pointerType !== "mouse") return;
        cancelScheduledClose();
        closeTimerRef.current = setTimeout(() => setOpen(false), 120);
    };

    useEffect(() => cancelScheduledClose, []);

    // The chrome fading out (rest mode) takes the panel with it.
    useEffect(() => {
        if (chromeHidden) setOpen(false);
    }, [chromeHidden]);

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    if (!captions.length) return null;

    const doneTracks = captions.filter((track) => track.status === "done");
    const generating = captions.some((track) => track.status === "processing");

    // Nothing finished: either still generating (inert button states it) or
    // everything failed (no affordance to offer — hide the button).
    if (!doneTracks.length) {
        if (!generating) return null;
        return (
            <Tooltip content={t("player.captions.generatingTooltip")} placement="top">
                <button
                    type="button"
                    aria-label={t("player.captions.generatingLabel")}
                    className="player-control-button player-captions-button player-captions-button--generating"
                    onClick={(event) => event.stopPropagation()}
                >
                    <IconLoader2 className="player-captions-spinner" />
                </button>
            </Tooltip>
        );
    }

    const renderOption = ({ key, label, checked, disabled, hint, onClick }) => (
        <button
            key={key}
            type="button"
            disabled={disabled}
            className="player-captions-option"
            onClick={(event) => {
                event.stopPropagation();
                onClick?.();
            }}
        >
            <span className="player-captions-check">{checked && <IconCheck size={16} />}</span>
            <Text.span >{label}</Text.span>
            {hint && !checked && <Text.small className="player-captions-hint">{hint}</Text.small>}
        </button>
    );

    const subtitleOptions = [
        renderOption({
            key: "captions-off",
            label: t("player.captions.off"),
            checked: !activeLang,
            onClick: () => onSelect(null),
        }),
        ...captions.map((track) => {
            const label = CAPTION_LANGUAGE_NAMES[track.language] ?? track.language;
            if (track.status === "processing") {
                return renderOption({
                    key: `captions-${track.language}`,
                    label,
                    disabled: true,
                    hint: t("player.captions.generating"),
                });
            }
            if (track.status !== "done") return null;
            const playable = availableLangs.includes(track.language);
            return renderOption({
                key: `captions-${track.language}`,
                label,
                checked: activeLang === track.language,
                disabled: !playable,
                hint: playable ? null : t("player.captions.unavailable"),
                onClick: () => onSelect(track.language),
            });
        }),
    ];

    return (
        <div
            className="player-captions"
            ref={rootRef}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
        >
            <Tooltip content={t("player.captions.language")} placement="top">
                <button
                    type="button"
                    aria-label={t("player.captions.language")}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-pressed={!!activeLang || undefined}
                    className={`player-control-button player-captions-button ${activeLang ? "active" : ""} ${open ? "open" : ""}`.trim()}
                    onClick={(event) => {
                        // Open-only: mouse users already opened via hover; a
                        // toggle here would close what the hover just opened.
                        // Touch relies on this, and closes via outside tap.
                        event.stopPropagation();
                        setOpen(true);
                    }}
                >
                    <span className="player-captions-portion">
                        <IconLanguage />
                        <span>{CAPTION_LANGUAGE_NAMES[naturalLanguage] ?? t("player.captions.original")}</span>
                    </span>
                    <span className="player-captions-trigger-divider" />
                    <span className="player-captions-portion">
                        {activeLang ? <IconMessageFilled /> : <IconMessage />}
                        <span>{activeLang ? CAPTION_LANGUAGE_NAMES[activeLang] : t("player.captions.off")}</span>
                    </span>
                </button>
            </Tooltip>

            {open && (
                <div className="player-captions-panel" onClick={(event) => event.stopPropagation()}>
                    <div className="player-captions-column">
                        <Text.h4 weight={400}>{t("player.captions.language")}</Text.h4>
                        {CAPTION_LANGUAGES.map((lang) =>
                            renderOption({
                                key: `audio-${lang}`,
                                label: CAPTION_LANGUAGE_NAMES[lang],
                                checked: lang === naturalLanguage,
                                disabled: true,
                                hint: t("common:status.comingSoon"),
                            })
                        )}
                    </div>

                    <div className="player-captions-column">
                        <Text.h4 weight={400}>{t("player.captions.subtitles")}</Text.h4>
                        {subtitleOptions}
                    </div>
                </div>
            )}
        </div>
    );
}

export default memo(CaptionsPanel);
